import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Button } from '@deepguide-ai/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@deepguide-ai/ui/dialog'
import { Input } from '@deepguide-ai/ui/input'
import { Label } from '@deepguide-ai/ui/label'
import { ScrollArea } from '@deepguide-ai/ui/scroll-area'
import { queryClient, trpc } from '@/lib/trpc'
import { vendorLogoPath } from '@/lib/vendor-logo'
import { useWorkspacePermissions } from '@/hooks/useWorkspacePermissions'
import { PersonnelSyncStatus } from '@/components/compliance/people/PersonnelSyncStatus'
import { ConfirmDialog } from '@/components/confirm-dialog'
import type { ConfigModalProps, IntegrationProvider } from '../types'

function AdpConfigModal({
  open,
  onOpenChange,
  onComplete,
  workspaceId,
}: ConfigModalProps) {
  const { canManage, isLoading: permissionsLoading } =
    useWorkspacePermissions(workspaceId)
  const [binding, setBinding] = useState<{
    code: string
    expiresAt: string
    marketplaceUrl: string
  } | null>(null)
  const [confirmDisconnect, setConfirmDisconnect] = useState(false)
  const status = useQuery({
    ...trpc.integrations.adp.connectionStatus.queryOptions(),
    enabled: open,
    refetchInterval: open ? 5000 : false,
  })
  const init = useMutation(
    trpc.integrations.adp.initConnection.mutationOptions({
      gcTime: 0,
      onMutate: () => setBinding(null),
      onSuccess: (data) => {
        setBinding(data)
        void status.refetch()
      },
    })
  )
  const finish = useMutation(
    trpc.integrations.adp.finishConnection.mutationOptions({
      onSuccess: () => {
        void status.refetch()
      },
    })
  )
  const sync = useMutation(
    trpc.employee.syncPersonnel.mutationOptions({
      onSuccess: () => {
        void status.refetch()
      },
    })
  )
  const disconnect = useMutation(
    trpc.integrations.deleteIntegration.mutationOptions({
      onSuccess: async () => {
        setBinding(null)
        init.reset()
        await queryClient.invalidateQueries({
          queryKey: trpc.integrations.adp.connectionStatus.queryKey(),
        })
        await queryClient.invalidateQueries({
          queryKey: trpc.employee.listPersonnelProviders.queryKey(),
        })
        setConfirmDisconnect(false)
        onComplete()
        onOpenChange(false)
      },
    })
  )
  const { isSuccess: codeIssued, reset: resetInit } = init
  // Keep the one-time code only in this open dialog, not in the mutation cache.
  useEffect(() => {
    if (codeIssued) resetInit()
  }, [codeIssued, resetInit])
  useEffect(() => {
    setBinding(null)
    setConfirmDisconnect(false)
  }, [open, workspaceId])
  const state = status.data?.state
  useEffect(() => {
    if (!state) return
    void queryClient.invalidateQueries({
      queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey(),
    })
    void queryClient.invalidateQueries({
      queryKey: trpc.integrations.getConnectedProviderKeys.queryKey(),
    })
    void queryClient.invalidateQueries({
      queryKey: trpc.integrations.listProviderConnections.queryKey(),
    })
    void queryClient.invalidateQueries({
      queryKey: trpc.employee.listPersonnelProviders.queryKey(),
    })
  }, [state, workspaceId])
  const queued =
    !!status.data?.jobState &&
    !['completed', 'failed', 'unknown'].includes(status.data.jobState)
  const pending =
    init.isPending || finish.isPending || sync.isPending || disconnect.isPending
  const disabled =
    !canManage ||
    permissionsLoading ||
    status.isPending ||
    status.isError ||
    pending ||
    queued
  const code =
    binding &&
    state === 'awaiting_subscription' &&
    binding.expiresAt === status.data?.codeExpiresAt &&
    Date.parse(binding.expiresAt) > Date.now()
      ? binding
      : null
  const canStart =
    !state ||
    ['inactive', 'invalid', 'expired', 'awaiting_subscription'].includes(state)
  const error = init.error || finish.error || sync.error

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(value) => {
          if (!value) {
            setBinding(null)
            init.reset()
            setConfirmDisconnect(false)
          }
          onOpenChange(value)
        }}
      >
        <DialogContent className='flex max-h-[85dvh] flex-col sm:max-w-lg'>
          <DialogHeader>
            <DialogTitle>ADP Workforce Now</DialogTitle>
            <DialogDescription>
              Connect your US Workforce Now subscription through ADP Marketplace
              to sync the authorized employee roster into People.
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className='min-h-0 flex-1' viewClassName='[&>div]:!block'>
            <div className='space-y-4 px-6 text-sm'>
              {!permissionsLoading && !canManage && (
                <p>
                  Only workspace admins can connect, sync or disconnect ADP
                  here.
                </p>
              )}
              {status.isPending && <p role='status'>Loading ADP connection…</p>}
              {status.isError && (
                <div>
                  <p role='alert' className='text-destructive'>
                    Could not load ADP connection status. Retry before making
                    changes.
                  </p>
                  <Button
                    variant='outline'
                    disabled={status.isFetching}
                    onClick={() => void status.refetch()}
                  >
                    Retry loading
                  </Button>
                </div>
              )}
              {status.data?.organizationOID && (
                <p>ADP organization: {status.data.organizationOID}</p>
              )}
              {canStart && status.isSuccess && (
                <p>
                  Generate a connection code, enter it in the Screenata
                  connection code field during Marketplace checkout, then
                  complete ADP’s data-access consent. Return here to finish
                  connecting.
                </p>
              )}
              {state === 'expired' && (
                <p>
                  The checkout code expired. Generate a new code before
                  continuing.
                </p>
              )}
              {state === 'awaiting_subscription' && !code && (
                <p>
                  The checkout code is shown only when generated. Generate a new
                  code if you no longer have it; the previous code will stop
                  working.
                </p>
              )}
              {code && canManage && (
                <div className='space-y-2'>
                  <Label htmlFor='adp-connection-code'>
                    Screenata connection code
                  </Label>
                  <Input
                    id='adp-connection-code'
                    readOnly
                    value={code.code}
                    autoComplete='off'
                    onFocus={(event) => event.currentTarget.select()}
                  />
                  <p className='text-muted-foreground'>
                    Expires {new Date(code.expiresAt).toLocaleTimeString()}.
                    Keep this code private; it links ADP to this workspace.
                    Generating a new code invalidates this one.
                  </p>
                  <Button asChild>
                    <a
                      href={code.marketplaceUrl}
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      Open ADP Marketplace
                    </a>
                  </Button>
                </div>
              )}
              {state === 'pending_consent' && (
                <p>
                  Marketplace checkout was received. After completing ADP’s
                  data-access consent, finish connecting to verify personnel
                  access. Setup runs in the background; you can close this
                  dialog.
                </p>
              )}
              {state === 'suspended' && (
                <p>
                  ADP suspended this subscription. Resolve it in ADP Marketplace
                  before finishing setup again.
                </p>
              )}
              {status.data && <PersonnelSyncStatus provider='adp' />}
              {error && (
                <p role='alert' className='text-destructive'>
                  {error.message}
                </p>
              )}
              {status.data && (
                <p className='text-muted-foreground'>
                  Personnel outside the authorized audience are retained.
                  Disconnecting stops future collection and retains imported
                  personnel; cancel the Marketplace subscription or revoke ADP
                  access separately.
                </p>
              )}
            </div>
          </ScrollArea>
          <DialogFooter className='flex-wrap'>
            {canManage && canStart && (
              <Button
                variant={code ? 'outline' : 'default'}
                disabled={disabled}
                onClick={() => init.mutate()}
              >
                {init.isPending
                  ? 'Generating…'
                  : binding ||
                      state === 'awaiting_subscription' ||
                      state === 'expired'
                    ? 'Generate new code'
                    : 'Generate connection code'}
              </Button>
            )}
            {canManage && state === 'pending_consent' && (
              <Button disabled={disabled} onClick={() => finish.mutate()}>
                {finish.isPending
                  ? 'Queuing setup…'
                  : queued
                    ? 'Setup in progress…'
                    : 'Finish connecting'}
              </Button>
            )}
            {canManage && state === 'active' && (
              <Button
                disabled={disabled || !status.data?.collectionReady}
                onClick={() => sync.mutate({ provider: 'adp' })}
              >
                {sync.isPending
                  ? 'Queuing sync…'
                  : queued
                    ? 'Sync in progress…'
                    : 'Sync personnel'}
              </Button>
            )}
            {status.data && (
              <Button variant='outline' asChild>
                <Link
                  to='/workspaces/$workspaceId/people'
                  params={{ workspaceId }}
                  onClick={() => onOpenChange(false)}
                >
                  Open People
                </Link>
              </Button>
            )}
            {canManage && status.data && state !== 'inactive' && (
              <Button
                variant='destructive'
                disabled={pending || status.isError}
                onClick={() => setConfirmDisconnect(true)}
              >
                Disconnect
              </Button>
            )}
            <Button variant='outline' onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={confirmDisconnect}
        onOpenChange={setConfirmDisconnect}
        destructive
        title='Disconnect ADP?'
        desc='Stop future ADP collection and invalidate pending setup. Imported personnel will remain. This does not cancel your ADP Marketplace subscription.'
        confirmText={disconnect.isPending ? 'Disconnecting…' : 'Disconnect ADP'}
        isLoading={disconnect.isPending}
        disabled={!canManage || !status.data || status.isError}
        handleConfirm={() => {
          if (status.data) disconnect.mutate({ id: status.data.integrationId })
        }}
      >
        {disconnect.error && (
          <p role='alert' className='text-destructive text-sm'>
            {disconnect.error.message}
          </p>
        )}
      </ConfirmDialog>
    </>
  )
}

export const AdpProvider: IntegrationProvider = {
  id: 'ADP',
  name: 'ADP Workforce Now',
  description: 'Sync authorized personnel and employment details into People',
  Icon: () => <img src={vendorLogoPath('adp')} alt='' className='size-6' />,
  ConfigModal: AdpConfigModal,
  postAuthAction: 'CONFIGURE',
  getFeatures: () => [
    {
      id: 'personnel',
      name: 'Personnel directory',
      description: 'Employment status, dates, departments and managers',
      enabled: true,
    },
  ],
  // The shared controller hosts Marketplace setup; opening it is not authorization success.
  handleConnect: async ({ onSuccess, onFinish }) => {
    onSuccess?.({ requiresSetup: true })
    onFinish?.('success')
  },
  syncData: async () => {
    await queryClient
      .getMutationCache()
      .build(queryClient, trpc.employee.syncPersonnel.mutationOptions())
      .execute({ provider: 'adp' })
    await queryClient.invalidateQueries({
      queryKey: trpc.integrations.adp.connectionStatus.queryKey(),
    })
  },
}
